import { Bot, Brain, Sparkles, Shield, Heart, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import privacyIcon from "@/assets/privacy-icon.png";
import halalIcon from "@/assets/halal-icon.png";
import verifiedIcon from "@/assets/verified-icon.png";

const Features = () => {
  const { t } = useTranslation();
  
  const mainFeatures = [
    {
      icon: privacyIcon,
      title: t('features.privacyFirst'),
      description: t('features.privacyFirstDesc'),
      gradient: "from-primary to-primary-light"
    },
    {
      icon: halalIcon,
      title: t('features.halalMatchmaking'),
      description: t('features.halalMatchmakingDesc'),
      gradient: "from-secondary to-secondary-light"
    },
    {
      icon: verifiedIcon,
      title: t('features.verifiedProfiles'),
      description: t('features.verifiedProfilesDesc'),
      gradient: "from-accent to-accent"
    }
  ];

  const aiFeatures = [
    { 
      icon: Bot, 
      title: t('features.aiBotDating'), 
      description: t('features.aiBotDatingDesc'),
      gradient: 'from-primary to-primary-light'
    },
    { 
      icon: Brain, 
      title: t('features.personalityMapping'), 
      description: t('features.personalityMappingDesc'),
      gradient: 'from-accent to-accent'
    },
    { 
      icon: Sparkles, 
      title: t('features.smartInsights'), 
      description: t('features.smartInsightsDesc'),
      gradient: 'from-secondary to-secondary-light'
    }
  ];

  return (
    <section id="features" className="py-24 sm:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      <div className="absolute inset-0 bg-gradient-mesh opacity-20" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[150px]" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20 stagger-children">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Why Choose Friendato</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-hero bg-clip-text text-transparent">
              Why Choose Friendato
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('features.whyChooseUsDesc')}
          </p>
        </div>

        {/* Main Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mb-24">
          {mainFeatures.map((feature, index) => (
            <div 
              key={index} 
              className="group glass-card p-8 sm:p-10 rounded-3xl text-center"
            >
              <div className="mb-8 flex justify-center">
                <div className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-soft group-hover:shadow-glow transition-all duration-500 group-hover:scale-110`}>
                  <img 
                    src={feature.icon} 
                    alt={feature.title} 
                    width={56}
                    height={56}
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14" 
                  />
                  {/* Glow ring */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-40 blur-xl transition-opacity`} />
                </div>
              </div>
              
              {/* Content */}
              <h3 className="text-xl sm:text-2xl font-bold mb-4 text-foreground">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* AI Features Section */}
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
              <Brain className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-accent">Powered by AI</span>
            </div>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                {t('features.aiPoweredMatching')}
              </span>
            </h3>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Advanced AI technology for smarter, deeper connections
            </p>
          </div>
          
          {/* AI Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {aiFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="group glass-card p-8 rounded-2xl"
                >
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 shadow-soft group-hover:shadow-glow transition-all duration-300 group-hover:scale-110`}>
                    <Icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  
                  {/* Content */}
                  <h4 className="font-bold text-xl mb-3 text-foreground">
                    {feature.title}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
